import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, FileText, Loader2, CheckCircle,
  AlertCircle, Info, Settings, RefreshCw
} from 'lucide-react';
import mammoth from 'mammoth';
import contratoConfigService from '../../services/contratoConfigService';
import { useNotifications } from '../../contexts/NotificationContext';

/* ──────────────────────────────────────────────────────────
   Estilos inyectados dentro del iframe para darle el look
   de un documento Word abierto (fondo gris, hoja blanca A4)
─────────────────────────────────────────────────────────── */
const WORD_IFRAME_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Calibri:wght@400;700&family=Times+New+Roman&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  html, body {
    margin: 0;
    padding: 0;
    background: #d0d0d0;
    font-family: 'Calibri', 'Arial', sans-serif;
    font-size: 11pt;
    color: #000;
  }

  /* Contenedor de páginas */
  .doc-pages {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 16px 40px;
    min-height: 100vh;
  }

  /* Página A4 */
  .doc-page {
    background: #fff;
    width: 21cm;
    min-height: 29.7cm;
    padding: 2.54cm 2.54cm 2.54cm 3.17cm;
    margin-bottom: 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.25);
    position: relative;
    overflow: hidden;
  }

  /* Tipografía Word */
  p  { margin: 0 0 6pt; line-height: 1.15; }
  h1 { font-size: 16pt; font-weight: bold; margin: 12pt 0 6pt; }
  h2 { font-size: 13pt; font-weight: bold; margin: 10pt 0 6pt; }
  h3 { font-size: 12pt; font-weight: bold; margin: 8pt 0 6pt; }
  h4,h5,h6 { font-size: 11pt; font-weight: bold; margin: 6pt 0 4pt; }
  ul, ol { margin: 0 0 6pt 0; padding-left: 36pt; }
  li { margin-bottom: 2pt; }
  a  { color: #1155CC; }

  /* Tablas */
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 8pt 0;
    font-size: 10pt;
  }
  td, th {
    border: 1px solid #000;
    padding: 4pt 6pt;
    vertical-align: top;
  }
  th {
    background: #f2f2f2;
    font-weight: bold;
  }

  /* Imágenes */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Centrado de contenido */
  .center { text-align: center; }
  .right  { text-align: right; }

  /* Encabezado / pie mammoth */
  .mammoth-table-of-contents-heading { display: none; }
`;

export default function PreviewPlantilla() {
  const { plantillaId } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  const iframeRef = useRef(null);

  const [plantilla, setPlantilla]       = useState(null);
  const [previewData, setPreviewData]   = useState(null);
  const [wordHtml, setWordHtml]         = useState(null);
  const [convirtiendo, setConvirtiendo] = useState(false);
  const [loading, setLoading]           = useState(true);
  const [descargando, setDescargando]   = useState(false);

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantillaId]);

  /* ── Carga metadata + preview base64 ── */
  const cargarDatos = async () => {
    try {
      setLoading(true);
      setWordHtml(null);

      const infoPlantilla = await contratoConfigService.obtenerPlantilla(plantillaId);
      setPlantilla(infoPlantilla);

      const previewRes = await contratoConfigService.previsualizarPlantilla(plantillaId);
      setPreviewData(previewRes);

      // Si es Word, convertir inmediatamente
      if (infoPlantilla.tipo_plantilla === 'word' && previewRes?.docxBase64) {
        await convertirDocxAHtml(previewRes.docxBase64);
      }
    } catch (error) {
      console.error('Error cargando preview:', error);
      showError('Error al cargar la previsualización de la plantilla');
    } finally {
      setLoading(false);
    }
  };

  /* ── Conversión docx → HTML con mammoth ── */
  const convertirDocxAHtml = async (base64) => {
    try {
      setConvirtiendo(true);

      // Decodificar base64 → ArrayBuffer
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const arrayBuffer = bytes.buffer;

      // Convertir con mammoth
      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          styleMap: [
            "p[style-name='Heading 1'] => h1:fresh",
            "p[style-name='Heading 2'] => h2:fresh",
            "p[style-name='Heading 3'] => h3:fresh",
            "b => strong",
            "i => em",
            "u => u",
          ],
          includeDefaultStyleMap: true,
          convertImage: mammoth.images.imgElement((image) => {
            return image.read('base64').then((base64Data) => ({
              src: `data:${image.contentType};base64,${base64Data}`,
            }));
          }),
        }
      );

      // Envolver el contenido en la estructura de página A4
      const fullHtml = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Preview</title>
  <style>${WORD_IFRAME_STYLES}</style>
</head>
<body>
  <div class="doc-pages">
    <div class="doc-page">
      ${result.value}
    </div>
  </div>
</body>
</html>`;

      setWordHtml(fullHtml);
    } catch (err) {
      console.error('Error convirtiendo docx a HTML:', err);
      showError('No se pudo renderizar el documento Word en el navegador');
    } finally {
      setConvirtiendo(false);
    }
  };

  /* ── Helpers de descarga ── */
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  };

  const handleDescargar = async () => {
    if (!plantilla || !previewData) return;
    try {
      setDescargando(true);

      if (previewData.tipo_plantilla === 'word' || previewData.docxBase64) {
        const mimeType =
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const blob = base64ToBlob(previewData.docxBase64, mimeType);
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = previewData.nombreArchivo || `${plantilla.nombre}_preview.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showSuccess('Archivo Word descargado exitosamente');
      } else {
        const blob = new Blob([previewData.html], { type: 'text/html' });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = `${plantilla.nombre}_preview.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showSuccess('Archivo HTML descargado exitosamente');
      }
    } catch (error) {
      console.error('Error al descargar preview:', error);
      showError('Error al descargar el archivo de previsualización');
    } finally {
      setDescargando(false);
    }
  };

  /* ── Estados de carga / error ── */
  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50 dark:bg-fodoBlack">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
        <p className="text-gray-500 dark:text-gray-300 font-medium">Cargando previsualización...</p>
      </div>
    );
  }

  if (!plantilla) {
    return (
      <div className="p-6 text-center bg-gray-50 dark:bg-fodoBlack min-h-screen flex flex-col justify-center items-center">
        <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No se encontró la plantilla</h2>
        <button
          onClick={() => navigate('/admin/plantillas')}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" /> Volver a Plantillas
        </button>
      </div>
    );
  }

  const esWord = plantilla.tipo_plantilla === 'word';

  return (
    <div className="flex flex-col bg-white dark:bg-fodoBlack min-h-screen">
      {/* ── Header ── */}
      <div className="px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-white dark:bg-fodoBlack shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/plantillas')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Volver"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600 dark:text-gray-300" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Previsualización de Plantilla
              </h1>
              <span
                className={`text-xs px-2 py-0.5 rounded font-semibold ${
                  esWord
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                }`}
              >
                {esWord ? 'Word (.docx)' : 'HTML'}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {plantilla.nombre}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Botón recargar */}
          <button
            onClick={cargarDatos}
            disabled={loading || convirtiendo}
            title="Recargar previsualización"
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${(loading || convirtiendo) ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Recargar</span>
          </button>

          {/* Botón descargar */}
          <button
            onClick={handleDescargar}
            disabled={descargando}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all shadow-sm hover:shadow disabled:bg-gray-400 dark:disabled:bg-gray-700"
          >
            {descargando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {descargando ? 'Descargando...' : `Descargar ${esWord ? 'Word' : 'HTML'}`}
          </button>
        </div>
      </div>

      {/* ── Body: sidebar + visor ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Sidebar colapsable en móvil */}
        <aside className="hidden lg:flex lg:flex-col w-72 xl:w-80 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-y-auto shrink-0">
          <div className="p-5 space-y-6">

            {/* Detalles */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-500" />
                Detalles de la Plantilla
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Nombre</p>
                  <p className="text-gray-900 dark:text-white font-medium">{plantilla.nombre}</p>
                </div>
                {plantilla.descripcion && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Descripción</p>
                    <p className="text-gray-600 dark:text-gray-300">{plantilla.descripcion}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Modo de firma</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300">
                      {plantilla.modo_firma === 'SOLO_ACEPTAR' ? 'Solo aceptar' : 'Firma digital'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Estado</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                      plantilla.activo ? 'text-green-600 dark:text-green-400' : 'text-red-500'
                    }`}>
                      <CheckCircle className="h-3 w-3" />
                      {plantilla.activo ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Creada el</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {new Date(plantilla.metadata?.fecha_creacion || plantilla.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Variables */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <Settings className="h-4 w-4 text-purple-500" />
                Variables ({plantilla.variables_usadas?.length || 0})
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Sustituidas dinámicamente con los datos de cada invitado.
              </p>
              {plantilla.variables_usadas?.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 max-h-52 overflow-y-auto pr-1">
                  {plantilla.variables_usadas.map((v, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded text-xs font-mono text-blue-800 dark:text-blue-300"
                    >
                      {esWord ? `{${v}}` : `{{${v}}}`}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No se detectaron variables.</p>
              )}
            </div>

          </div>
        </aside>

        {/* ── Área de previsualización ── */}
        <main className="flex-1 overflow-hidden bg-[#c8c8c8] dark:bg-[#2a2a2a] relative">

          {/* Spinner de conversión superpuesto */}
          {convirtiendo && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#c8c8c8]/80 dark:bg-[#2a2a2a]/80 backdrop-blur-sm">
              <Loader2 className="h-10 w-10 animate-spin text-blue-500 mb-3" />
              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                Renderizando documento Word...
              </p>
            </div>
          )}

          {esWord ? (
            /* ── Visor Word (mammoth → iframe) ── */
            wordHtml ? (
              <iframe
                ref={iframeRef}
                srcDoc={wordHtml}
                title="Vista previa del documento Word"
                className="w-full h-full border-0"
                style={{ minHeight: 'calc(100vh - 65px)' }}
                sandbox="allow-same-origin"
              />
            ) : !convirtiendo ? (
              /* Fallback: sin HTML todavía y no convirtiendo */
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <FileText className="h-16 w-16 text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  No se pudo generar la previsualización del documento.
                </p>
                <button
                  onClick={() => previewData?.docxBase64 && convertirDocxAHtml(previewData.docxBase64)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <RefreshCw className="h-4 w-4" /> Intentar de nuevo
                </button>
              </div>
            ) : null
          ) : (
            /* ── Visor HTML ── */
            previewData?.html ? (
              <iframe
                srcDoc={previewData.html}
                title="Vista previa de plantilla HTML"
                className="w-full border-0"
                style={{ minHeight: 'calc(100vh - 65px)', height: '100%' }}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-400 italic">No se pudo cargar el preview HTML.</p>
              </div>
            )
          )}
        </main>
      </div>
    </div>
  );
}
